import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { ORG } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm text-hecso-ink focus:outline-none focus:ring-2 focus:ring-hecso-green focus:border-hecso-green transition";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent successfully. We will get back to you soon.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <PageHeader
        title="Contact Us"
        subtitle="Reach out for partnerships, program enquiries, volunteering or any questions about our work."
      />

      <section className="py-16 sm:py-20 bg-white">
        <div className="container-hecso grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-4">
            {[
              { icon: MapPin, title: "Head Office", lines: [ORG.address] },
              { icon: Phone, title: "Phone", lines: [ORG.phone, ORG.phone2] },
              { icon: Mail, title: "Email", lines: [ORG.email] },
              { icon: Clock, title: "Office Hours", lines: ["Monday – Saturday", "9:30 AM – 5:30 PM"] },
            ].map((c, i) => (
              <Reveal key={c.title} delay={i * 80}>
                <div className="flex gap-4 rounded-xl border border-slate-200 bg-hecso-beige p-5" data-testid={`contact-info-${i}`}>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-hecso-green text-white">
                    <c.icon className="w-5 h-5" />
                  </span>
                  <div>
                    <h3 className="font-heading text-sm font-semibold text-hecso-ink">{c.title}</h3>
                    {c.lines.map((l, j) => (
                      <p key={j} className="mt-0.5 text-sm text-hecso-muted">{l}</p>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={150} className="lg:col-span-3">
            <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-hecso-beige p-6 sm:p-8 shadow-sm space-y-5" data-testid="contact-form">
              <h2 className="font-heading text-xl font-semibold text-hecso-ink">Send Us a Message</h2>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-hecso-ink mb-1.5">Full Name *</label>
                  <input required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" data-testid="contact-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-hecso-ink mb-1.5">Email *</label>
                  <input required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" data-testid="contact-email-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-hecso-ink mb-1.5">Phone</label>
                  <input value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+91 XXXXX XXXXX" data-testid="contact-phone-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-hecso-ink mb-1.5">Subject *</label>
                  <select required value={form.subject} onChange={set("subject")} className={inputCls} data-testid="contact-subject-select">
                    <option value="">Select a subject</option>
                    <option>General Enquiry</option>
                    <option>Program Partnership</option>
                    <option>Volunteering</option>
                    <option>Donation Query</option>
                    <option>Media & Press</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-hecso-ink mb-1.5">Message *</label>
                <textarea required rows={5} value={form.message} onChange={set("message")} className={inputCls} placeholder="How can we help you?" data-testid="contact-message-input" />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-btn"
                className="w-full sm:w-auto rounded-lg bg-hecso-green px-8 py-3 text-sm font-semibold text-white transition-all hover:bg-hecso-greendark hover:-translate-y-0.5 active:scale-95 disabled:opacity-60"
              >
                {submitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>

      <section className="pb-16 sm:pb-20 bg-white" data-testid="map-section">
        <div className="container-hecso">
          <Reveal>
            <div className="overflow-hidden rounded-2xl border border-slate-200 shadow-sm">
              <iframe
                title="HECSO Head Office — Guwahati, Assam"
                src="https://www.openstreetmap.org/export/embed.html?bbox=91.71%2C26.12%2C91.82%2C26.19&layer=mapnik&marker=26.1552%2C91.7659"
                className="h-[380px] w-full"
                loading="lazy"
                data-testid="contact-map"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
